import * as React from 'react';
import {
    AutocompleteInput,
    DateInput,
    NumberInput,
    SelectInput,
    TextInput,
    useChoicesContext,
} from 'react-admin';
import { Box, Typography } from '@mui/material';

import { SEARCH_PAGE_SIZE, SearchMorePaper, SearchMoreProvider } from './searchDropdown';

/** Odoo-style bold compact labels (shared: product profile, customer, company). */
export const FIELD_LABEL_SX = {
    fontWeight: 700,
    fontSize: '0.8rem',
    color: '#212529',
    minWidth: 140,
    lineHeight: '30px',
};

export const FIELD_VALUE_SX = {
    fontSize: '0.8rem',
    color: '#212529',
    lineHeight: '30px',
};

/** Bottom border only — `variant="standard"` compact inputs */
export const UNDERLINE_FIELD_SX = {
    mb: 0,
    '& .MuiFormHelperText-root': { display: 'none' },
    '& .MuiInputBase-root': { fontSize: '0.8rem', minHeight: 30 },
    '& .MuiInputBase-input': { py: '5px' },
    '& .MuiInputLabel-root': { display: 'none' },
    // Chrome paints an autofilled input as a filled, bordered box, which breaks the underline look
    // of a whole row of fields. The long transition is the standard way to keep the browser's
    // background from ever landing.
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
        {
            WebkitBoxShadow: '0 0 0 1000px transparent inset',
            WebkitTextFillColor: 'inherit',
            caretColor: 'inherit',
            transition: 'background-color 9999s ease-in-out 0s',
        },
};

const FIELD_LABEL_COL_PX = 220;
const FIELD_LABEL_COL_PX_XS = 140;

const BREAKPOINT_ORDER = ['xs', 'sm', 'md', 'lg', 'xl'] as const;
type FieldRowBreakpoint = (typeof BREAKPOINT_ORDER)[number];

/** A fixed label column, or per-breakpoint widths (missing breakpoints inherit the one below). */
export type FieldRowLabelWidth = number | Partial<Record<FieldRowBreakpoint, number>>;

/** Fills every breakpoint by carrying the previous value forward, the way MUI resolves `sx` maps. */
function resolveLabelCols(labelWidth?: FieldRowLabelWidth): Record<FieldRowBreakpoint, number> {
    const requested: Partial<Record<FieldRowBreakpoint, number>> =
        labelWidth == null
            ? { xs: FIELD_LABEL_COL_PX_XS, sm: FIELD_LABEL_COL_PX }
            : typeof labelWidth === 'number'
              ? { xs: labelWidth }
              : { xs: FIELD_LABEL_COL_PX_XS, sm: FIELD_LABEL_COL_PX, ...labelWidth };

    let carried = FIELD_LABEL_COL_PX;
    const out = {} as Record<FieldRowBreakpoint, number>;
    for (const bp of BREAKPOINT_ORDER) {
        if (requested[bp] != null) carried = requested[bp] as number;
        out[bp] = carried;
    }
    return out;
}

/** Drops breakpoints that repeat the previous value, so the emitted `sx` stays small. */
function dedupeResponsive<T>(values: Record<FieldRowBreakpoint, T>): Partial<Record<FieldRowBreakpoint, T>> {
    const out: Partial<Record<FieldRowBreakpoint, T>> = {};
    let previous: T | undefined;
    for (const bp of BREAKPOINT_ORDER) {
        if (previous === undefined || values[bp] !== previous) out[bp] = values[bp];
        previous = values[bp];
    }
    return out;
}

export function FieldRow({
    label,
    children,
    alignItems = 'center',
    dense = false,
    labelWidth,
    stackBelow,
}: {
    label: React.ReactNode;
    children: React.ReactNode;
    alignItems?: 'center' | 'flex-start';
    dense?: boolean;
    /** Narrower label column for side-by-side record blocks (Odoo address / contact columns). */
    labelWidth?: FieldRowLabelWidth;
    /**
     * Below this breakpoint the label sits *above* a full-width input instead of beside it.
     * Opt-in: without it the row keeps the original label-beside-input flex layout at every
     * width, which a fixed label column turns into a sliver of input space on a phone.
     */
    stackBelow?: Exclude<FieldRowBreakpoint, 'xs'>;
}) {
    const cols = resolveLabelCols(labelWidth);
    const stackIndex = stackBelow ? BREAKPOINT_ORDER.indexOf(stackBelow) : 0;

    const labelFlex = {} as Record<FieldRowBreakpoint, string>;
    const labelWidthSx = {} as Record<FieldRowBreakpoint, string | number>;
    const labelPr = {} as Record<FieldRowBreakpoint, number>;
    BREAKPOINT_ORDER.forEach((bp, i) => {
        const stacked = i < stackIndex;
        labelFlex[bp] = stacked ? '0 0 auto' : `0 0 ${cols[bp]}px`;
        labelWidthSx[bp] = stacked ? '100%' : cols[bp];
        labelPr[bp] = stacked ? 0 : 2;
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: stackBelow ? { xs: 'column', [stackBelow]: 'row' } : 'row',
                alignItems: stackBelow ? { xs: 'stretch', [stackBelow]: alignItems } : alignItems,
                minHeight: dense ? 28 : 34,
                mb: dense ? '2px' : '4px',
                // Only for opt-in rows, so every existing caller keeps its exact previous layout.
                ...(stackBelow ? { minWidth: 0 } : null),
            }}
        >
            <Typography
                component="div"
                sx={{
                    ...FIELD_LABEL_SX,
                    flex: dedupeResponsive(labelFlex),
                    width: dedupeResponsive(labelWidthSx),
                    maxWidth: dedupeResponsive(labelWidthSx),
                    minWidth: 0,
                    pr: dedupeResponsive(labelPr),
                    boxSizing: 'border-box',
                    alignSelf: stackBelow
                        ? { xs: 'flex-start', [stackBelow]: alignItems === 'flex-start' ? 'flex-start' : 'center' }
                        : alignItems === 'flex-start'
                          ? 'flex-start'
                          : 'center',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.3,
                    py: dense ? '0px' : '2px',
                    fontSize: dense ? '0.78rem' : FIELD_LABEL_SX.fontSize,
                }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    width: '100%',
                    ...FIELD_VALUE_SX,
                    lineHeight: dense ? '26px' : FIELD_VALUE_SX.lineHeight,
                    '& .RaReferenceInput-root': { width: '100%', display: 'block' },
                    '& .ra-input': { width: '100%', maxWidth: '100%' },
                    '& .MuiFormControl-root': { width: '100%', maxWidth: '100%' },
                    '& .MuiAutocomplete-root': { width: '100%', maxWidth: '100%' },
                    '& .MuiTextField-root': { width: '100%', maxWidth: '100%' },
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

export const CompactTextInput = (props: React.ComponentProps<typeof TextInput>) => (
    <TextInput
        {...props}
        variant="standard"
        margin="none"
        size="small"
        sx={{
            ...UNDERLINE_FIELD_SX,
            ...(props.sx as object),
        }}
    />
);

export const CompactNumberInput = (props: React.ComponentProps<typeof NumberInput>) => (
    <NumberInput
        {...props}
        variant="standard"
        margin="none"
        size="small"
        sx={{
            ...UNDERLINE_FIELD_SX,
            ...(props.sx as object),
        }}
    />
);

export const CompactSelectInput = (props: React.ComponentProps<typeof SelectInput>) => (
    <SelectInput
        {...props}
        variant="standard"
        margin="none"
        size="small"
        sx={{
            ...UNDERLINE_FIELD_SX,
            '& .MuiSelect-select': { py: '5px' },
            '& .MuiSelect-icon': { top: 'calc(50% - 12px)' },
            ...(props.sx as object),
        }}
    />
);

export const CompactDateInput = (props: React.ComponentProps<typeof DateInput>) => (
    <DateInput
        {...props}
        variant="standard"
        margin="none"
        size="small"
        sx={{
            ...UNDERLINE_FIELD_SX,
            ...(props.sx as object),
        }}
    />
);

/**
 * react-admin AutocompleteInput with the Products-search behaviour (common/searchDropdown): the
 * first SEARCH_PAGE_SIZE matches, then a "Search more..." row that shows the next page — asking
 * the ReferenceInput for a bigger page when the loaded choices run out. Typing starts over at the
 * first page. Works with static `choices` too.
 */
export const SearchAutocompleteInput = (props: React.ComponentProps<typeof AutocompleteInput>) => {
    const { onInputChange, slots, suggestionLimit, ...rest } = props as React.ComponentProps<typeof AutocompleteInput> & {
        slots?: Record<string, unknown>;
    };
    const choices = useChoicesContext({
        choices: props.choices as never,
        resource: props.resource,
        source: props.source,
    });
    const [pages, setPages] = React.useState(1);
    const limit = SEARCH_PAGE_SIZE * pages;
    const loaded = choices.allChoices?.length ?? 0;
    const total = typeof choices.total === 'number' ? choices.total : undefined;
    const hasMore = suggestionLimit == null && (loaded > limit || (total != null && total > loaded));
    const { setPerPage, perPage } = choices;
    const onMore = React.useCallback(() => {
        const nextLimit = limit + SEARCH_PAGE_SIZE;
        setPages(p => p + 1);
        if (total != null && total > loaded && loaded < nextLimit && setPerPage)
            setPerPage(Math.max(perPage ?? 0, nextLimit + SEARCH_PAGE_SIZE));
    }, [limit, total, loaded, setPerPage, perPage]);

    return (
        <SearchMoreProvider hasMore={hasMore} onMore={onMore}>
            <AutocompleteInput
                {...rest}
                suggestionLimit={suggestionLimit ?? limit}
                slots={{ paper: SearchMorePaper, ...(slots ?? {}) }}
                onInputChange={(event, value, reason) => {
                    if (reason === 'input' || reason === 'clear') setPages(1);
                    onInputChange?.(event, value, reason);
                }}
            />
        </SearchMoreProvider>
    );
};

export const CompactAutocompleteInput = (props: React.ComponentProps<typeof AutocompleteInput>) => (
    <SearchAutocompleteInput
        {...props}
        variant="standard"
        margin="none"
        size="small"
        sx={{
            ...UNDERLINE_FIELD_SX,
            width: '100%',
            maxWidth: '100%',
            '& .MuiFormControl-root': { width: '100%', maxWidth: '100%' },
            '& .MuiTextField-root': { width: '100%', maxWidth: '100%' },
            '& .MuiAutocomplete-inputRoot': { minHeight: 30, py: '2px' },
            '& .MuiAutocomplete-input': { py: '5px !important' },
            ...(props.sx as object),
        }}
    />
);

/**
 * Measures a document title's rendered width so a sibling field-label column can align to it
 * (Odoo-style document header: labels right-align flush with the title above them).
 */
export function useDocumentTitleInputStart(displayText: string) {
    const titleRef = React.useRef<HTMLElement | null>(null);
    const [startPx, setStartPx] = React.useState(120);

    React.useLayoutEffect(() => {
        const el = titleRef.current;
        if (!el) return;
        const update = () => setStartPx(Math.ceil(el.getBoundingClientRect().width) + 2);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        window.addEventListener('resize', update);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', update);
        };
    }, [displayText]);

    return { titleRef, startPx };
}

const DOCUMENT_FIELD_ROW_SX = {
    display: 'grid',
    columnGap: '8px',
    rowGap: '2px',
    alignItems: 'center',
    minHeight: 28,
    mb: '2px',
} as const;

const DOCUMENT_FIELD_LABEL_SX = {
    ...FIELD_LABEL_SX,
    fontSize: '0.78rem',
    lineHeight: 1.3,
    minWidth: 0,
    pr: 1,
    whiteSpace: 'nowrap',
} as const;

const DOCUMENT_FIELD_VALUE_SX = {
    ...FIELD_VALUE_SX,
    minWidth: 0,
    width: '100%',
    lineHeight: '26px',
    '& .RaReferenceInput-root': { width: '100%', display: 'block' },
    '& .ra-input': { width: '100%', maxWidth: '100%' },
    '& .MuiFormControl-root': { width: '100%', maxWidth: '100%' },
    '& .MuiAutocomplete-root': { width: '100%', maxWidth: '100%' },
    '& .MuiTextField-root': { width: '100%', maxWidth: '100%' },
} as const;

/** Document header field row: label right-aligned to a dynamically measured title-width column (see {@link useDocumentTitleInputStart}). */
export function DocumentFieldRow({
    label,
    children,
    labelColPx,
}: {
    label: React.ReactNode;
    children: React.ReactNode;
    labelColPx: number;
}) {
    return (
        <Box
            sx={{
                ...DOCUMENT_FIELD_ROW_SX,
                gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', sm: `${labelColPx}px minmax(0, 1fr)` },
            }}
        >
            <Typography component="div" sx={{ ...DOCUMENT_FIELD_LABEL_SX, textAlign: { xs: 'left', sm: 'right' } }}>
                {label}
            </Typography>
            <Box sx={DOCUMENT_FIELD_VALUE_SX}>{children}</Box>
        </Box>
    );
}

export const UNDERLINE_FIELD_WITH_HELPER_SX = {
    mb: 0,
    '& .MuiInputBase-root': { fontSize: '0.8rem', minHeight: 30 },
    '& .MuiInputBase-input': { py: '5px' },
    '& .MuiInputLabel-root': { display: 'none' },
    '& .MuiFormHelperText-root': {
        fontSize: '0.7rem',
        mt: 0.5,
        mx: 0,
    },
};
